import { ProductPackagingVal } from '@app/shared/_models/product';
import { nowIso } from '@app/shared/time';

// Miroir de la réponse de GET /search/scan (mercalink-server/search, crate scan) ; sens des champs : documentation.html#server.scan.
export type ScanStandard = 'empty' | 'plain' | 'gs1_linear' | 'gs1_element_string' | 'gs1_digital_link' | 'hibc' | 'iso15434' | 'isbt128';

export interface ScanGtin {
  gtin14: string;
  stored: string;
  form: 'gtin8' | 'gtin12' | 'gtin13' | 'gtin14';
  check_ok: boolean;
  indicator: number;
  hint: string | null;
}

export interface ScanElement {
  ai: string;
  title: string;
  value: string;
}

export interface ScanWarning {
  code: string;
  detail: string;
}

export interface ScanHibc {
  lic: string;
  product_code: string;
  unit_of_measure: number;
  primary: string;
  udi_di: string;
  check: string;
  check_ok: boolean;
  lot: string | null;
  serial: string | null;
  expiry: string | null;
  manufacture_date: string | null;
  quantity: number | null;
}

// Ce que la lecture EST, décidé par le serveur : product = un code article ; link = URL de fournisseur (site, fiche, vidéo), pas un code produit ;
// logistic = SSCC de carton ; partial = lot/péremption/série sans article ; unknown = illisible ; empty = rien.
export type ScanNature = 'product' | 'link' | 'logistic' | 'partial' | 'unknown' | 'empty';

export interface Scan {
  raw: string;
  data: string;
  aim: string | null;
  symbology: string | null;
  standard: ScanStandard;
  nature: ScanNature;
  gtin: ScanGtin | null;
  add_on: string | null;
  sscc: string | null;
  content: ScanGtin | null;
  lot: string | null;
  serial: string | null;
  expiry: string | null;
  best_before: string | null;
  production_date: string | null;
  count: number | null;
  measures: { ai: string; title: string; value: number }[];
  prices: { ai: string; title: string; amount: number; currency: string | null }[];
  elements: ScanElement[];
  hibc: ScanHibc | null;
  warnings: ScanWarning[];
  keys: string[];
}

export interface ScanResult {
  scan: Scan;
  key: string | null;
  ppv: ProductPackagingVal | null;
}

// « 3x… » / « 3*… » (1 à 999, sans zéro de tête) = quantité de saisie ; au-delà c'est une chaîne GS1 sans séparateur (01…10X…).
// Le chiffre est obligatoire : sans lui un code interne « X0042 » perdrait sa première lettre.
export function splitQtyPrefix(text: string): { qty: number; raw: string } {
  const m = text.match(/^([1-9]\d{0,2})[xX*](\S.*)$/);
  return m ? { qty: +m[1], raw: m[2].trim() } : { qty: 1, raw: text };
}

// Vrai quand rien n'a été reconnu ET que ce n'est pas un simple nombre : c'est un texte de recherche, pas un code.
export function looksLikeFreeText(scan: Scan, raw: string): boolean {
  return scan.standard === 'plain' && /[^\d]/.test(raw);
}

// Message quand la lecture n'est pas un code produit (QR de fournisseur vers son site, SSCC de carton, étiquette secondaire) ; null sinon.
// À tester AVANT looksLikeFreeText : une URL est du texte pour lui, et ouvrirait la recherche produit sur « https://… ».
export function scanRejection(scan: Scan, raw: string): string | null {
  switch (scan.nature) {
    case 'link': {
      let host = '';
      try {
        host = new URL(/^www\./i.test(raw) ? `https://${raw}` : raw).hostname.replace(/^www\./, '');
      } catch {
        /* pas une URL lisible : message sans le site */
      }
      return `Ce QR code est un lien vers un site${host ? ` (${host})` : ''}, pas un code produit : scannez un autre code-barres sur l’emballage`;
    }
    case 'logistic':
      return 'Ce code est celui du carton (SSCC), pas de l’article : scannez le code-barres du produit';
    case 'partial':
      return 'Ce code ne porte que lot et péremption, sans l’article : scannez le code-barres du produit';
    default:
      return null;
  }
}

// Libellé d'une lecture non trouvée : ce que le serveur a compris (GTIN, HIBC, lot, péremption), sinon la lecture elle-même.
export function scanLabel(scan: Scan, raw: string): string {
  const id = scan.gtin?.stored ?? scan.hibc?.udi_di ?? scan.sscc ?? raw;
  const parts = [id];
  if (scan.standard !== 'plain' && scan.standard !== 'empty') parts.push(`[${scan.standard}]`);
  if (scan.lot) parts.push(`lot ${scan.lot}`);
  if (scan.expiry) parts.push(`péremption ${scan.expiry}`);
  if (scan.gtin && !scan.gtin.check_ok) parts.push('clé de contrôle fausse');
  return parts.join(', ');
}

// Identifiants de production d'une unité vendue (UDI-PI), rangés dans sales_order_l.data.udi : documentation.html#server.sales.udi-ligne.
// Le serveur redécode `raw` avant d'écrire ; ce que le front pose ici n'est qu'une prévisualisation.
export interface Udi {
  raw?: string;
  standard?: string;
  gtin14?: string | null;
  lot?: string | null;
  expiry?: string | null;
  best_before?: string | null;
  production_date?: string | null;
  serial?: string | null;
  udi_di?: string | null;
  read_at?: string;
}

const PI_FIELDS: (keyof Udi)[] = ['lot', 'expiry', 'best_before', 'production_date', 'serial'];

// Un `udi` seulement quand la lecture porte au moins un identifiant de production ; un EAN-13 nu n'en produit pas.
export function udiFromScan(scan: Scan): Udi | null {
  const udi: Udi = {
    raw: scan.raw,
    standard: scan.standard,
    gtin14: scan.gtin?.gtin14 ?? null,
    lot: scan.lot,
    expiry: scan.expiry,
    best_before: scan.best_before,
    production_date: scan.production_date,
    serial: scan.serial,
    udi_di: scan.hibc?.udi_di ?? null,
    read_at: nowIso(),
  };
  return PI_FIELDS.some(k => udi[k]) ? udi : null;
}

// Deux lectures ne se cumulent sur une même ligne que si elles désignent le même lot ; une série impose une ligne par unité.
export function sameUdi(a: Udi | null | undefined, b: Udi | null | undefined): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.serial || b.serial) return false;
  return PI_FIELDS.every(k => (a[k] ?? null) === (b[k] ?? null)) && (a.gtin14 ?? null) === (b.gtin14 ?? null);
}

// « lot LOTA1B2 · exp. 2027-06-30 · n° SN…» pour la ligne du ticket ; vide sans identifiant.
export function udiLabel(udi: Udi | null | undefined): string {
  if (!udi) return '';
  const parts: string[] = [];
  if (udi.lot) parts.push('lot ' + udi.lot);
  if (udi.expiry) parts.push('exp. ' + udi.expiry);
  if (udi.best_before) parts.push('DLUO ' + udi.best_before);
  if (udi.production_date) parts.push('fab. ' + udi.production_date);
  if (udi.serial) parts.push('n° ' + udi.serial);
  return parts.join(' · ');
}
